import React, { useState, useEffect } from 'react';
import { Plug, Boxes, Plus, Users } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

function Franquicias() {
  const [franchises, setFranchises] = useState([]);
  const [newName, setNewName] = useState('');
  const [newCity, setNewCity] = useState('');
  const [cityOptions, setCityOptions] = useState([]);

  useEffect(() => {
    api.getFranchisesFresh().then(setFranchises).catch(() => {});
    api.getValueLists().then((lists) => setCityOptions(lists.find((l) => l.id === 'ciudades')?.values.filter((v) => v.active) || [])).catch(() => {});
  }, []);

  const toggle = async (f) => {
    if (f.id === 'matriz') { toast.error('MATRIZ no puede desactivarse'); return; }
    try {
      const updated = await api.updateFranchise(f.id, { active: !f.active });
      setFranchises((prev) => prev.map((x) => x.id === f.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim() || !newCity) { toast.error('Nombre y ciudad son obligatorios'); return; }
    const cityLabel = cityOptions.find((c) => c.id === newCity)?.label || newCity;
    try {
      const created = await api.createFranchise({ name: newName.trim(), type: 'franquicia', city: cityLabel, active: true });
      setFranchises((prev) => [...prev, created]);
      setNewName(''); setNewCity('');
      toast.success('Franquicia creada');
    } catch { toast.error('Error al crear'); }
  };

  return (
    <div data-testid="admin-franquicias">
      <h1 className="text-2xl font-black text-stone-800 mb-1">Franquicias</h1>
      <p className="text-sm text-stone-500 mb-6">Cada producto y pedido pertenece a MATRIZ o a una franquicia. Aislamiento total de datos.</p>

      <form onSubmit={add} className="bg-white rounded-lg border border-stone-200 p-5 mb-6 flex flex-wrap gap-3 items-end max-w-3xl">
        <div className="flex-1 min-w-52">
          <label className="block text-xs font-bold text-stone-500 mb-1">Nombre</label>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Natur Autoescuela Getafe" className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="franchise-name-input" />
        </div>
        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1">Ciudad</label>
          <select value={newCity} onChange={(e) => setNewCity(e.target.value)} className="border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="franchise-city-select">
            <option value="">Selecciona…</option>
            {cityOptions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <button type="submit" className="btn-natur flex items-center gap-1.5 px-4 py-2.5 rounded font-bold text-sm" data-testid="add-franchise-button">
          <Plus className="w-4 h-4" /> Crear
        </button>
      </form>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden max-w-3xl">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Nombre</th>
              <th className="px-4 py-3 font-medium">Tipo</th>
              <th className="px-4 py-3 font-medium">Ciudad</th>
              <th className="px-4 py-3 font-medium w-24">Activa</th>
            </tr>
          </thead>
          <tbody>
            {franchises.map((f) => (
              <tr key={f.id} className="border-t border-stone-100" data-testid={`franchise-row-${f.id}`}>
                <td className="px-4 py-3 font-bold text-stone-800">{f.name}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${f.type === 'matriz' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>{f.type.toUpperCase()}</span>
                </td>
                <td className="px-4 py-3 text-stone-600">{f.city}</td>
                <td className="px-4 py-3"><Switch checked={f.active} onCheckedChange={() => toggle(f)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Usuarios() {
  return (
    <div data-testid="admin-usuarios">
      <h1 className="text-2xl font-black text-stone-800 mb-1">Usuarios</h1>
      <p className="text-sm text-stone-500 mb-6">Estructura preparada para roles MATRIZ → FRANQUICIA → CLIENTE FINAL.</p>
      <div className="bg-white rounded-lg border border-stone-200 p-8 max-w-2xl text-center">
        <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
        <p className="text-sm text-stone-500">La gestión de usuarios y accesos por franquicia se activará en la siguiente fase.<br />Los clientes compran como invitados (sin registro), según directriz de checkout.</p>
      </div>
    </div>
  );
}

function Integraciones() {
  return (
    <div data-testid="admin-integraciones">
      <h1 className="text-2xl font-black text-stone-800 mb-1">Integraciones</h1>
      <p className="text-sm text-stone-500 mb-6">Configuración mediante variables de entorno (test / producción).</p>
      <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center"><Plug className="w-5 h-5 text-indigo-500" /></div>
          <div>
            <h2 className="font-black text-stone-800">Stripe</h2>
            <p className="text-xs text-stone-500">Pagos con tarjeta</p>
          </div>
          <span className="ml-auto text-xs font-bold bg-stone-100 text-stone-500 px-3 py-1.5 rounded-full">Pendiente de configurar</span>
        </div>
        <div className="bg-stone-50 rounded p-4 text-xs text-stone-500 font-mono space-y-1">
          <p>STRIPE_SECRET_KEY = (no configurada)</p>
          <p>STRIPE_PUBLISHABLE_KEY = (no configurada)</p>
          <p>STRIPE_MODE = test</p>
        </div>
        <p className="text-xs text-stone-400 mt-4">Arquitectura preparada en backend/.env. Cuando se faciliten las claves, la pasarela se activará sin cambios en catálogo ni checkout.</p>
      </div>
    </div>
  );
}

function Scme() {
  const [settings, setSettings] = useState(null);
  const [total, setTotal] = useState('');
  const [chatStatus, setChatStatus] = useState(null);
  const [chatStats, setChatStats] = useState(null);

  useEffect(() => {
    api.getScmeSettings().then((s) => { setSettings(s); setTotal(String(s.approvedCounterTotal ?? 0)); }).catch(() => {});
    api.getChatStatus().then(setChatStatus).catch(() => {});
    api.getChatStats().then(setChatStats).catch(() => {});
  }, []);

  const toggleSetting = async (key) => {
    try {
      const updated = await api.updateScmeSettings({ [key]: !settings[key] });
      setSettings(updated);
      toast.success('Ajuste actualizado');
    } catch { toast.error('Error al actualizar'); }
  };

  const saveTotal = async () => {
    try {
      const updated = await api.updateScmeSettings({ approvedCounterTotal: parseInt(total, 10) || 0 });
      setSettings(updated);
      setTotal(String(updated.approvedCounterTotal));
      toast.success('Total de aprobados guardado');
    } catch { toast.error('Error al guardar'); }
  };

  const modules = [
    { name: 'Cupones', status: 'Planificado' },
    { name: 'Afiliados', status: 'Planificado' },
    { name: 'Newsletter', status: 'Planificado' },
    { name: 'Reviews', status: 'Planificado' },
    { name: 'Tarjetas regalo', status: 'Parcial (producto)' },
    { name: 'Fidelización', status: 'Planificado' },
    { name: 'Sistema documental', status: 'Parcial (Área Legal)' },
    { name: 'LMS', status: 'Planificado' },
    { name: 'IA', status: 'Planificado' },
  ];
  return (
    <div data-testid="admin-scme">
      <h1 className="text-2xl font-black text-stone-800 mb-1">Ecosistema SCME</h1>
      <p className="text-sm text-stone-500 mb-6">Catálogo corporativo de módulos reutilizables. Estructura preparada.</p>

      {settings && (
        <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-4xl mb-8" data-testid="scme-settings-panel">
          <h2 className="font-black text-stone-800 mb-1">Funciones de portada</h2>
          <p className="text-xs text-stone-500 mb-5">Interruptores globales de la tienda. Los cambios se aplican al instante.</p>
          <div className="space-y-5">
            <div className="flex items-center justify-between border-b border-stone-100 pb-5">
              <div>
                <p className="font-bold text-stone-800 text-sm">Vídeos en opiniones</p>
                <p className="text-xs text-stone-500">Muestra el vídeo del alumno aprobado en las tarjetas de opinión (si la opinión tiene vídeo asignado).</p>
              </div>
              <Switch checked={!!settings.reviewVideosEnabled} onCheckedChange={() => toggleSetting('reviewVideosEnabled')} data-testid="scme-toggle-review-videos" />
            </div>
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="font-bold text-stone-800 text-sm">Contador de aprobados</p>
                <p className="text-xs text-stone-500 mb-3">Muestra en portada el total de alumnos aprobados desde la inauguración. Total gestionado a mano por la autoescuela.</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={total}
                    onChange={(e) => setTotal(e.target.value)}
                    className="border border-stone-300 rounded px-3 py-2 text-sm w-32 focus:outline-none focus:ring-2 focus:ring-amber-400"
                    data-testid="scme-approved-total-input"
                  />
                  <button onClick={saveTotal} className="btn-natur px-4 py-2 rounded text-xs font-bold" data-testid="scme-approved-total-save">Guardar total</button>
                </div>
              </div>
              <Switch checked={!!settings.approvedCounterEnabled} onCheckedChange={() => toggleSetting('approvedCounterEnabled')} data-testid="scme-toggle-approved-counter" />
            </div>
            <div className="flex items-start justify-between gap-6 border-t border-stone-100 pt-5" data-testid="scme-ana-block">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold text-stone-800 text-sm">Agente ANA (burbuja de chat)</p>
                  {chatStatus && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${chatStatus.available ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-500'}`} data-testid="ana-provider-status">
                      {chatStatus.available ? `Proveedor: ${chatStatus.provider}` : 'Sin proveedor conectado'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-500 mb-3">Cliente del servicio conversacional corporativo (App.NaturAutoescuela.es). La tienda solo consume: la inteligencia, memoria y leads residen fuera. Conexión vía CHAT_PROVIDER / ANA_API_URL / ANA_API_KEY en backend/.env.</p>
                {chatStats && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-lg" data-testid="ana-stats">
                    {[['open', 'Aperturas'], ['conversation_started', 'Conversaciones'], ['contact_request', 'Solicitudes contacto'], ['lead', 'Leads']].map(([k, label]) => (
                      <div key={k} className="bg-stone-50 border border-stone-200 rounded p-3 text-center">
                        <p className="font-black text-stone-800 text-lg" data-testid={`ana-stat-${k}`}>{chatStats[k] ?? 0}</p>
                        <p className="text-[10px] text-stone-500">{label}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[11px] text-stone-400 mt-2">Contadores anónimos (solo tipo + fecha). Cualquier analítica avanzada deberá respetar Consentimientos → Servicios Digitales.</p>
              </div>
              <Switch checked={settings.anaEnabled !== false} onCheckedChange={() => toggleSetting('anaEnabled')} data-testid="scme-toggle-ana" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
        {modules.map((m, i) => (
          <div key={i} className="bg-white rounded-lg border border-stone-200 p-5 flex items-center gap-3">
            <Boxes className="w-6 h-6 text-stone-400" />
            <div>
              <p className="font-bold text-stone-800 text-sm">{m.name}</p>
              <p className="text-xs text-stone-400">{m.status}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminSistema({ section }) {
  if (section === 'franquicias') return <Franquicias />;
  if (section === 'usuarios') return <Usuarios />;
  if (section === 'integraciones') return <Integraciones />;
  return <Scme />;
}
